import { useState, useEffect, useRef, useMemo } from "react";

/**
 * Progressive loading hook for index pages.
 *
 * Stages:
 * 1. Skeleton — items array is empty (API pending). Caller renders skeleton cards.
 * 2. Batch   — first `batchSize` items are exposed so the browser paints the
 *              above-the-fold content immediately.
 * 3. Stream  — the remaining items are revealed in `chunkSize` increments,
 *              one chunk per animation frame, so long lists never block the
 *              first paint with a single monolithic render commit. (The old
 *              behavior revealed EVERYTHING on the very next frame, which
 *              meant the browser rarely got to paint the first batch before
 *              the full-list render seized the main thread.)
 *
 * If items are already populated on first render (e.g. SSR data) the hook
 * starts in the batch phase and streams from there.
 *
 * Pass `cacheLoading = true` while the initial localStorage cache read is still
 * in-flight (i.e. before the first cache check attempt completes). This prevents
 * skeleton cards from flashing during the brief window between mount and the first
 * cache read, so back-navigation from a detail page never shows the skeleton at all.
 */
export function useProgressiveLoading<T>(
    items: T[],
    batchSize = 10,
    /** When true, stops showing skeletons even if items is empty (e.g. query errored). */
    settled = false,
    /**
     * When true, the cache check is still in progress — suppress skeletons so
     * the user sees a blank state instead of skeleton cards that immediately
     * disappear once the cache arrives. Defaults to false (backward-compatible).
     */
    cacheLoading = false,
    /** How many additional items each streaming frame reveals. */
    chunkSize = 24,
): {
    visibleItems: T[];
    showSkeletons: boolean;
    skeletonCount: number;
} {
    const [revealCount, setRevealCount] = useState(batchSize);
    const hadItemsOnce = useRef(items.length > 0);
    const hasItems = items.length > 0;
    const total = items.length;

    useEffect(() => {
        if (!hasItems) return;
        hadItemsOnce.current = true;
        if (revealCount >= total) return;
        // Reveal the next chunk on the next frame; the effect re-runs after
        // each state change until the whole list is exposed. Each chunk is
        // its own render commit, so the first batch gets painted and the
        // rest streams in without a long main-thread block.
        const id = requestAnimationFrame(() =>
            setRevealCount((prev) => Math.min(prev + chunkSize, total)),
        );
        return () => cancelAnimationFrame(id);
    }, [hasItems, revealCount, total, chunkSize]);

    // Show skeletons only when:
    //  • no items yet
    //  • never had items (avoids showing skeletons on filter → 0 results)
    //  • the cache read has fully completed (not still loading)
    //  • the data source hasn't settled to an errored / empty state
    const showSkeletons =
        !hasItems && !hadItemsOnce.current && !cacheLoading && !settled;

    const visibleItems = useMemo(() => {
        if (!hasItems) return [] as T[];
        if (revealCount >= total) return items;
        return items.slice(0, revealCount);
    }, [hasItems, revealCount, total, items]);

    return {
        visibleItems,
        showSkeletons,
        skeletonCount: batchSize,
    };
}
